import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Models, PackageType } from "api/types";

import { ListingSortOptions, sortOptions } from "./FilterAndSortListings";

export interface FilterCriteria {
  concerns: string[];
  areas: string[];
  brands: string[];
  productTypes: string[];
  fulfillmentOptions: string[];
}

export interface FilterAndSortOptions {
  sort: ListingSortOptions;
  view?: string;
}

export const filterAndSortRetailProducts = (
  products: Models<"DisplayRetailProductListing">[],
  criteria: FilterCriteria,
  options: FilterAndSortOptions
) => {
  const { concerns, areas, brands, productTypes, fulfillmentOptions } =
    criteria;
  const { sort, view = "all" } = options;
  if (products.length === 0) return products;

  const brandsSet = new Set(brands);

  const isNewArrival = (
    product: Models<"DisplayRetailProductListing">
  ): boolean => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const productDate = new Date(product.createdAtUtc || 0);
    return productDate >= thirtyDaysAgo;
  };

  const matchesConcerns = (
    product: Models<"DisplayRetailProductListing">
  ): boolean => {
    if (concerns.length === 0) return true;
    if (!product.concernIds) return false;
    return concerns.some((concernId) => product.concernIds.includes(concernId));
  };

  const matchesAreas = (
    product: Models<"DisplayRetailProductListing">
  ): boolean => {
    if (areas.length === 0) return true;
    if (!product.areaIds) return false;
    return areas.some((areaId) => product.areaIds.includes(areaId));
  };

  const matchesBrands = (
    product: Models<"DisplayRetailProductListing">
  ): boolean => {
    if (brands.length === 0) return true;
    if (!product.brandId) return false;
    return brandsSet.has(product.brandId);
  };

  const matchesProductTypes = (
    product: Models<"DisplayRetailProductListing">
  ): boolean => {
    if (productTypes.length === 0) return true;
    if (!product.productTypeIds) return false;
    return productTypes.some((productTypeId) =>
      product.productTypeIds.includes(productTypeId)
    );
  };

  const matchesFulfillmentOptions = (
    product: Models<"DisplayRetailProductListing">
  ): boolean => {
    if (fulfillmentOptions.length === 0) return true;

    const hasInStore =
      fulfillmentOptions.includes("Product") &&
      product.type === PackageType.Product;
    const hasDelivery =
      fulfillmentOptions.includes("ProductFromExternalSource") &&
      product.type === PackageType.ProductFromExternalSource;

    return hasInStore || hasDelivery;
  };

  const filtered = products.filter((product) => {
    if (view === "new-arrivals") {
      return isNewArrival(product);
    }
    if (view === "featured") {
      return product.isFeaturedProduct;
    }
    if (view === "best-selling") {
      return product.isBestSeller;
    }

    return (
      matchesConcerns(product) &&
      matchesAreas(product) &&
      matchesBrands(product) &&
      matchesProductTypes(product) &&
      matchesFulfillmentOptions(product)
    );
  });

  return filtered.sort((a, b) => {
    if (view === "new-arrivals") {
      const dateA = new Date(a.createdAtUtc || 0);
      const dateB = new Date(b.createdAtUtc || 0);
      return dateB.getTime() - dateA.getTime();
    } else if (view === "featured") {
      return a.oneTimePurchasePriceInCents - b.oneTimePurchasePriceInCents;
    } else if (view === "best-selling") {
      return a.oneTimePurchasePriceInCents - b.oneTimePurchasePriceInCents;
    }

    switch (sort) {
      case "featured": {
        return 0;
      }
      case "priceLowToHigh": {
        return a.oneTimePurchasePriceInCents - b.oneTimePurchasePriceInCents;
      }
      case "priceHighToLow": {
        return b.oneTimePurchasePriceInCents - a.oneTimePurchasePriceInCents;
      }
      case "recentlyAdded": {
        const dateA = new Date(a.createdAtUtc || 0);
        const dateB = new Date(b.createdAtUtc || 0);
        return dateB.getTime() - dateA.getTime();
      }
      case "onsale": {
        return 0;
      }
      case "bestsellers": {
        if (a.isBestSeller && !b.isBestSeller) return -1;
        if (!a.isBestSeller && b.isBestSeller) return 1;
        return 0;
      }
      default: {
        return 0;
      }
    }
  });
};

export const useRetailProductFilterAndSort = (
  products: Models<"DisplayRetailProductListing">[]
) => {
  const stringifyArray = (strings: string[] | null) => strings?.join(",") ?? "";
  const joinStrings = (strings: string | null) =>
    strings === "" ? [] : strings?.split(",") ?? [];

  const [searchParams, setSearchParams] = useSearchParams();

  const availableFulfillmentOptions = useMemo(() => {
    const options = [];
    const hasInStoreProducts = products.some(
      (product) => product.type === PackageType.Product
    );
    const hasDeliveryProducts = products.some(
      (product) => product.type === PackageType.ProductFromExternalSource
    );

    if (hasInStoreProducts) {
      options.push("Product");
    }
    if (hasDeliveryProducts) {
      options.push("ProductFromExternalSource");
    }

    return options;
  }, [products]);

  const hasSingleFulfillmentOption = availableFulfillmentOptions.length === 1;

  const urlFilters = useMemo(
    () => ({
      view: searchParams.get("view") || "all",
      concerns: joinStrings(searchParams.get("concerns")),
      areas: joinStrings(searchParams.get("areas")),
      fulfillmentOptions: joinStrings(searchParams.get("fulfillmentOptions")),
      brands: joinStrings(searchParams.get("brands")),
      productTypes: joinStrings(searchParams.get("productTypes")),
      sort: (searchParams.get("sort") as ListingSortOptions) || "featured",
    }),
    [searchParams]
  );

  const [pendingFilters, setPendingFilters] = useState(urlFilters);
  const [appliedFilters, setAppliedFilters] = useState(urlFilters);
  const [checkpoints, setCheckpoints] = useState(urlFilters);

  const filteredRetailProducts = useMemo(() => {
    return filterAndSortRetailProducts(
      products,
      {
        concerns: appliedFilters.concerns,
        areas: appliedFilters.areas,
        brands: appliedFilters.brands,
        productTypes: appliedFilters.productTypes,
        fulfillmentOptions: appliedFilters.fulfillmentOptions,
      },
      {
        sort: appliedFilters.sort,
        view: appliedFilters.view,
      }
    );
  }, [products, appliedFilters]);

  const hasListingsWithDiscounts = false;
  const filteredSortOptions = hasListingsWithDiscounts
    ? sortOptions
    : new Map([...sortOptions].filter(([key]) => key !== "onsale"));

  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  }, [pendingFilters, appliedFilters]);

  const hasActiveFilters = useMemo(() => {
    return (
      appliedFilters.concerns.length > 0 ||
      appliedFilters.areas.length > 0 ||
      appliedFilters.brands.length > 0 ||
      appliedFilters.productTypes.length > 0 ||
      appliedFilters.fulfillmentOptions.length > 0
    );
  }, [appliedFilters]);

  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      view: "all",
      concerns: [],
      areas: [],
      fulfillmentOptions: [],
      brands: [],
      productTypes: [],
      sort: "featured" as ListingSortOptions,
    };
    setPendingFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
  };

  const createArrayHandler =
    (filterType: keyof typeof pendingFilters) => (id: string) => {
      const current = pendingFilters[filterType] as string[];
      const newValue = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      setPendingFilters((prev) => ({ ...prev, [filterType]: newValue }));
    };

  const clearFilter = (filterType: keyof typeof pendingFilters) => () => {
    const cleared = { ...pendingFilters, [filterType]: [] };
    setPendingFilters(cleared);
    setSearchParams({
      view: appliedFilters.view,
      areas: stringifyArray(appliedFilters.areas),
      concerns: stringifyArray(appliedFilters.concerns),
      sort: appliedFilters.sort,
      fulfillmentOptions: stringifyArray(appliedFilters.fulfillmentOptions),
      brands: stringifyArray(appliedFilters.brands),
      productTypes: stringifyArray(appliedFilters.productTypes),
      [filterType]: "",
    });
  };

  return {
    filteredListings: filteredRetailProducts,
    view: appliedFilters.view,
    setView: (newView: string) => {
      setPendingFilters((prev) => ({ ...prev, view: newView }));
    },
    concerns: appliedFilters.concerns,
    setConcerns: createArrayHandler("concerns"),
    areas: appliedFilters.areas,
    setAreas: createArrayHandler("areas"),
    fulfillmentOptions: appliedFilters.fulfillmentOptions,
    setFulfillmentOptions: createArrayHandler("fulfillmentOptions"),
    availableFulfillmentOptions,
    hasSingleFulfillmentOption,
    brands: appliedFilters.brands,
    setBrands: createArrayHandler("brands"),
    productTypes: appliedFilters.productTypes,
    setProductTypes: createArrayHandler("productTypes"),
    sort: appliedFilters.sort,
    sortBy: (newSort: ListingSortOptions) => {
      setPendingFilters((prev) => ({ ...prev, sort: newSort }));
    },
    sortOptions: filteredSortOptions,
    setSearchParams: () =>
      setSearchParams({
        view: appliedFilters.view,
        areas: stringifyArray(appliedFilters.areas),
        concerns: stringifyArray(appliedFilters.concerns),
        sort: appliedFilters.sort,
        fulfillmentOptions: stringifyArray(appliedFilters.fulfillmentOptions),
        brands: stringifyArray(appliedFilters.brands),
        productTypes: stringifyArray(appliedFilters.productTypes),
      }),
    saveCheckpoints: () => setCheckpoints(appliedFilters),
    restore: () => {
      setPendingFilters(checkpoints);
      setAppliedFilters(checkpoints);
    },
    clearConcerns: clearFilter("concerns"),
    clearAreas: clearFilter("areas"),
    clearFulfillmentOptions: clearFilter("fulfillmentOptions"),
    clearBrands: clearFilter("brands"),
    clearProductTypes: clearFilter("productTypes"),
    applyFilters,
    clearFilters,
    hasPendingChanges,
    hasActiveFilters,
    pendingFilters,
  };
};
